import type { Game } from './game';
import type { Noise } from './entities';
import type { FloorId } from '@/world/types';
import type { Floor } from '@/world/builder';
import { hasLos } from '@/gfx/lighting';
import { TILE } from '@/gfx/tiles';

type Position = { floor: FloorId; x: number; y: number };

/** Loud impacts can carry through an adjoining stairwell, with a finite loss.
 * Floors are separate spaces: matching map coordinates never imply proximity.
 */
export function stairSoundDistance(g: Game, listener: Position, noise: Noise): number {
  if (noise.r < 180 || noise.kind === 'step' || noise.floor === listener.floor) return Infinity;
  const from = g.mansion.get(noise.floor), to = g.mansion.get(listener.floor);
  const leg = (f: Floor, a: { x: number; y: number }, b: { x: number; y: number }) => {
    const d = Math.hypot(a.x - b.x, a.y - b.y);
    // Walls and shut doors muffle the sound on either side of the stairwell.
    return hasLos(a.x, g.eyeY(a), b.x, g.eyeY(b), g.reachSegs(f)) ? d : d * 1.8 + 48;
  };
  let best = Infinity;
  for (const stair of from.stairs) {
    if (stair.def.toFloor !== to.id) continue;
    const landing = to.stairs.find(s => s.def.id === stair.def.to);
    if (!landing) continue;
    const entrance = { x: (stair.entry.x + 0.5) * TILE, y: (stair.entry.y + 0.5) * TILE };
    const exit = { x: (landing.entry.x + 0.5) * TILE, y: (landing.entry.y + 0.5) * TILE };
    const sourceDistance = leg(from, noise, entrance);
    if (sourceDistance + 96 > noise.r) continue;
    best = Math.min(best, sourceDistance + 96 + leg(to, listener, exit));
  }
  return best;
}
